/**
 * Play together again.
 *
 * The squad you had a good evening with on Tuesday is the single most useful
 * list in a game app, and until now Pugo threw it away the moment the party
 * closed. This reads it back off the parties themselves: who you were in one
 * with, when, and what you were playing.
 *
 * Two things you can do from here, which is the whole point: add somebody you
 * are not friends with yet, or get the old group back into a party in one
 * press rather than four.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { Party, Teammate } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { ago } from '@/lib/notifications';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { UserName, openUserCard } from './user';
import { AvailableTag } from './Availability';
import { Button, useToast } from './ui';

export function useRecentTeammates(enabled = true) {
  const me = useSession((s) => s.me);
  return useQuery({
    queryKey: ['teammates', 'recent'],
    queryFn: () => api.get<{ people: Teammate[] }>('/api/teammates/recent'),
    enabled: enabled && !!me,
    staleTime: 60_000
  });
}

export function Teammates({ limit = 4, className }: { limit?: number; className?: string }) {
  const { data, isPending } = useRecentTeammates();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const [invited, setInvited] = useState<string[]>([]);
  const [added, setAdded] = useState<string[]>([]);

  /* The party to invite into, if there is one. Without it the button says
     what it would do instead of failing when pressed. */
  const mine = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' }),
    staleTime: 30_000
  });
  const party: Party | undefined = (mine.data?.parties ?? []).find((p) => !p.closed);

  const people = (data?.people ?? []).slice(0, limit);

  async function invite(t: Teammate) {
    if (!party || busy) return;
    setBusy(t.user.id);
    try {
      await api.post('/api/parties/' + party.id + '/invite', { username: t.user.username });
      setInvited((l) => [...l, t.user.id]);
      toast('Invited ' + t.user.displayName, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy('');
    }
  }

  /** Everyone on the list at once - "get the band back together". */
  async function inviteAll() {
    if (!party || busy) return;
    setBusy('all');
    let sent = 0;
    for (const t of people) {
      if (invited.includes(t.user.id)) continue;
      try {
        await api.post('/api/parties/' + party.id + '/invite', { username: t.user.username });
        sent += 1;
        setInvited((l) => [...l, t.user.id]);
      } catch { /* one refusal - somebody is already in a party - is not a failure of the rest */ }
    }
    setBusy('');
    toast(sent ? 'Invited ' + sent + (sent === 1 ? ' player' : ' players') : 'Nobody new to invite', sent ? 'good' : 'bad');
  }

  async function addFriend(t: Teammate) {
    if (busy) return;
    setBusy(t.user.id);
    try {
      await api.post('/api/friends/request', { username: t.user.username });
      setAdded((l) => [...l, t.user.id]);
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
      toast('Friend request sent to ' + t.user.displayName, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy('');
    }
  }

  if (!isPending && !people.length) return null;

  return (
    <section
      aria-labelledby="teammates-title"
      className={cn('pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]', className)}
    >
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="history" size={19} className="text-ink" />
        <h2 id="teammates-title" className="m-0 flex-1 text-[17px] font-bold text-ink">Play together again</h2>
        {party && people.length > 1 ? (
          <Button size="sm" kind="outline" disabled={!!busy} onClick={() => void inviteAll()}>
            {busy === 'all' ? 'Inviting…' : 'Invite all'}
          </Button>
        ) : null}
      </div>

      {isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-[56px] animate-pulse rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2" />)}
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {people.map((t) => {
            const waiting = busy === t.user.id;
            return (
              <li
                key={t.user.id}
                className="flex items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2"
              >
                <button
                  type="button"
                  onClick={(e) => openUserCard(t.user, e.currentTarget)}
                  aria-label={'Show profile card of ' + t.user.displayName}
                  className="flex-none"
                >
                  <Avatar user={t.user} size={36} presence />
                </button>

                <span className="min-w-0 flex-1">
                  <UserName user={t.user} className="block truncate text-[13.5px] font-semibold" />
                  <span className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-mute">
                    <GameMark id={t.gameId} size={14} plain />
                    <span className="truncate">
                      {t.times > 1 ? t.times + ' parties · ' : ''}{ago(t.lastPlayed)}
                    </span>
                  </span>
                </span>

                <AvailableTag available={t.user.available} className="hidden flex-none @sm:inline-flex" />

                {!t.isFriend && !added.includes(t.user.id) ? (
                  <Button size="sm" kind="plain" disabled={waiting} onClick={() => void addFriend(t)}>
                    {waiting ? '…' : 'Add'}
                  </Button>
                ) : null}

                {party ? (
                  invited.includes(t.user.id) ? (
                    <span className="flex-none rounded-full bg-surface-3 px-2.5 py-1 text-[12px] font-semibold text-dim">Invited</span>
                  ) : (
                    <Button size="sm" kind="outline" disabled={waiting} onClick={() => void invite(t)}>
                      {waiting ? '…' : 'Invite'}
                    </Button>
                  )
                ) : (
                  <Button size="sm" kind="outline" onClick={() => go('chat/' + t.user.username)}>Message</Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!party && people.length ? (
        <p className="mt-2.5 mb-0 text-[12px] text-mute">
          Open a party and this list turns into one-press invitations.
        </p>
      ) : null}
    </section>
  );
}
